import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Grid, TextField } from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { YearGroup } from '../../types';

const yearGroupSchema = z.object({
  name: z.string().min(1, 'Year group name is required'),
  capacity: z.coerce.number().min(1, 'Capacity must be at least 1'),
});

type YearGroupFormData = z.infer<typeof yearGroupSchema>;

interface YearGroupFormProps {
  yearGroup?: YearGroup;
  onSubmit: (data: YearGroup) => void;
  onCancel: () => void;
}

export const YearGroupForm: React.FC<YearGroupFormProps> = ({ yearGroup, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<YearGroupFormData>({
    resolver: zodResolver(yearGroupSchema),
    defaultValues: {
      name: yearGroup?.name || '',
      capacity: yearGroup?.capacity || 180,
    },
  });

  const handleFormSubmit = (data: YearGroupFormData) => {
    onSubmit({
      ...data,
      id: yearGroup?.id || `year-group-${Date.now()}`,
      currentSize: yearGroup?.currentSize || 0,
    } as YearGroup);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Year Group Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message || 'e.g., Year 7, Year 12'}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="capacity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Capacity"
                type="number"
                fullWidth
                error={!!errors.capacity}
                helperText={errors.capacity?.message || 'Maximum number of students'}
                required
              />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {yearGroup ? 'Update' : 'Create'} Year Group
        </Button>
      </Box>
    </form>
  );
};
